'use client';

import { 
  ControlType, 
  CalculatorId, 
  CalculatorType, 
  ControlIds, 
  type CalculatorConfig, 
  type ControlValues,
  type CalculatorControl,
  type SingleColumnSelectControl,
  type TextControl,
  type SelectControl,
  type NumberControl
} from '../../../lib/calculators/calculator-config';
import { TableDataJSON } from '../../../src/types/table-json';
import { LazyTabLabECharts } from '@repo/ui';
import type { EChartsOption, ScatterSeriesOption, LineSeriesOption } from 'echarts';
import { linearRegression, polynomialRegression, exponentialRegression } from '../../../lib/stats';

// Type for scatter chart specific control values
export interface ScatterChartControlValues extends ControlValues {
  [ControlIds.XColumn]: string;
  [ControlIds.YColumn]: string;
  [ControlIds.Title]: string;
  [ControlIds.MarkerSize]: number;
  [ControlIds.TrendlineType]: 'none' | 'linear' | 'polynomial' | 'exponential';
}

// Define the controls for the scatter chart
export const scatterChartControls = {
  tabs: [
    {
      title: 'Data',
      controls: [
        {
          id: ControlIds.YColumn,
          type: ControlType.SingleColumnSelect,
          label: 'Y-axis column',
          required: true,
          numericOnly: true
        } as SingleColumnSelectControl,
        {
          id: ControlIds.XColumn,
          type: ControlType.SingleColumnSelect,
          label: 'X-axis column',
          required: true,
          numericOnly: true
        } as SingleColumnSelectControl,
        {
          id: ControlIds.Title,
          type: ControlType.Text,
          label: 'Chart title'
        } as TextControl
      ] as readonly CalculatorControl[]
    },
    {
      title: 'Appearance',
      controls: [
        {
          id: ControlIds.MarkerSize,
          type: ControlType.Number,
          label: 'Marker size',
          min: 4,
          max: 20,
          step: 1,
          defaultValue: 8
        } as NumberControl,
        {
          id: ControlIds.TrendlineType,
          type: ControlType.Select,
          label: 'Trend line',
          options: [
            { label: 'None', value: 'none' },
            { label: 'Linear', value: 'linear' },
            { label: 'Polynomial', value: 'polynomial' },
            { label: 'Exponential', value: 'exponential' }
          ],
          defaultValue: 'none'
        } as SelectControl
      ] as readonly CalculatorControl[]
    }
  ]
};

function calculateTrendline(data: [number, number][], type: string): readonly [number, number][] {
  const x = data.map(([x]) => x);
  const y = data.map(([_, y]) => y);

  let regression;
  switch (type) {
    case 'linear':
      regression = linearRegression({ x, y });
      break;
    case 'polynomial':
      regression = polynomialRegression({ x, y });
      break;
    case 'exponential':
      regression = exponentialRegression({ x, y });
      break;
    default:
      return [];
  }

  return regression.points;
}

function calculateStatistics(data: [number, number][], type: string): { 
  equation: string;
  r2: number;
  r: number;
} {
  const x = data.map(([x]) => x);
  const y = data.map(([_, y]) => y);

  switch (type) {
    case 'linear':
      return linearRegression({ x, y });
    case 'polynomial':
      return polynomialRegression({ x, y });
    case 'exponential':
      return exponentialRegression({ x, y });
    default:
      return { equation: '', r2: 0, r: 0 };
  }
}

// Function to get scatter chart configuration
function getScatterChartConfig({
  data,
  title,
  markerSize,
  trendlineType
}: {
  data: [number, number][];
  title: string;
  markerSize: number;
  trendlineType: string;
}): EChartsOption {
  const series: (ScatterSeriesOption | LineSeriesOption)[] = [{
    type: 'scatter',
    data,
    symbolSize: markerSize * 1.2,
    emphasis: {
      focus: 'series',
      itemStyle: {
        shadowBlur: 10,
        shadowColor: 'rgba(0, 0, 0, 0.5)'
      }
    }
  }];

  let stats;
  if (trendlineType !== 'none') {
    const trendlineData = calculateTrendline(data, trendlineType);
    stats = calculateStatistics(data, trendlineType);
    series.push({
      type: 'line',
      data: [...trendlineData],
      symbol: 'none',
      lineStyle: {
        type: 'dashed'
      }
    });
  }

  return {
    title: {
      text: title,
      left: 'center',
      ...(stats ? {
        subtext: `${stats.equation}\nR² = ${stats.r2.toFixed(3)}, r = ${stats.r.toFixed(3)}`,
        subtextStyle: {
          color: '#666',
          fontSize: 12
        }
      } : {})
    },
    tooltip: {
      trigger: 'axis',
      axisPointer: {
        type: 'cross'
      },
      formatter: (params: any) => {
        const point = params[0];
        const xAxisName = point.dimensionNames[0] || 'X';
        const yAxisName = point.dimensionNames[1] || 'Y';
        return `${xAxisName}: ${point.data[0]}<br/>${yAxisName}: ${point.data[1]}`;
      }
    },
    toolbox: {
      show: true,
      feature: {
        saveAsImage: {
          title: 'Save as Image',
          name: 'scatter-plot'
        },
        dataZoom: {
          title: {
            zoom: 'Zoom',
            back: 'Zoom Reset'
          }
        }
      }
    },
    grid: {
      top: 60,
      right: 40,
      bottom: 60,
      left: 60
    },
    xAxis: {
      type: 'value',
      scale: true
    },
    yAxis: {
      type: 'value',
      scale: true
    },
    series
  };
}

export const scatterChartConfig: CalculatorConfig<CalculatorId.ScatterChart> = {
  id: CalculatorId.ScatterChart,
  type: CalculatorType.Graph,
  name: 'Scatter Plot',
  initialData: TableDataJSON.from({
    columns: [
      { 
        id: 'x', 
        name: 'X Value', 
        values: ['1', '2', '3', '4', '5', '6', '7', '8', '9', '10'] 
      },
      { 
        id: 'y', 
        name: 'Y Value', 
        // Quadratic pattern (x^2) with significant random noise
        values: [
          '1.8',    // ~1 + noise
          '5.1',    // ~4 + noise
          '8.2',    // ~9 - noise
          '18.5',   // ~16 + noise
          '22.3',   // ~25 - noise
          '38.9',   // ~36 + noise
          '45.2',   // ~49 - noise
          '68.7',   // ~64 + noise
          '75.4',   // ~81 - noise
          '105.8'   // ~100 + noise
        ] 
      }
    ]
  }),
  initialControlValues: {
    xColumn: 'x',
    yColumn: 'y',
    title: 'Quadratic Growth',
    markerSize: 10,
    trendlineType: 'none'
  },
  controls: scatterChartControls,
  getInitialConfig: (data: TableDataJSON) => {
    const numericColumns = data.columns.filter(col => 
      col.values.every(val => !isNaN(Number(val)))
    );

    const firstColumn = numericColumns[0];
    const secondColumn = numericColumns[1];

    return {
      xColumn: firstColumn?.id ?? '',
      yColumn: secondColumn?.id ?? '',
      title: firstColumn && secondColumn
        ? `${secondColumn.name} vs ${firstColumn.name}`
        : 'Scatter Plot',
      markerSize: 10,
      trendlineType: 'none'
    };
  },
  getVisualizationData: ({ data, controls }) => {
    const scatterControls = controls as ScatterChartControlValues;
    
    if (!data?.columns?.length || !scatterControls[ControlIds.XColumn] || !scatterControls[ControlIds.YColumn]) {
      console.log('Missing data or controls:', { data, controls });
      return null;
    }

    const xColumn = data.columns.find(col => col.id === scatterControls[ControlIds.XColumn]);
    const yColumn = data.columns.find(col => col.id === scatterControls[ControlIds.YColumn]);

    if (!xColumn || !yColumn) {
      console.log('X or Y column not found:', { x: scatterControls[ControlIds.XColumn], y: scatterControls[ControlIds.YColumn] });
      return null;
    }

    // Create data array for scatter plot
    const scatterData: [number, number][] = xColumn.values.map((x, index) => {
      const xVal = Number(x) || 0;
      const yVal = Number(yColumn.values[index]) || 0;
      return [xVal, yVal];
    });

    return getScatterChartConfig({
      data: scatterData,
      title: scatterControls[ControlIds.Title],
      markerSize: scatterControls[ControlIds.MarkerSize],
      trendlineType: scatterControls[ControlIds.TrendlineType]
    });
  },
  render: ({ visualizationData }) => 
    visualizationData ? (
      <LazyTabLabECharts option={visualizationData} className="h-full w-full" />
    ) : null
}; 